/**
 * Downtown Strip shop interiors — Pizza Palace, Retro Arcade, Vinyl Vault,
 * Ink & Iron, Boba Break. Each is a walkable single-room interior with an
 * NPC, themed decor and a working counter (order/play/listen).
 */

export const SHOPS = {
  pizza: {
    num: 901, kind: 'pizza', name: '🍕 Pizza Palace', wall: '#3b1a12', floor: '#8a5a34',
    npc: 'Tony', npcTitle: 'Tony the Pizzaiolo', accent: '#ef4444',
    line: 'Hot slice, fresh outta the oven!',
    menu: [
      { id: 'pizza_slice', name: 'Cheese Slice', icon: '🍕', price: 10, kind: 'food' },
      { id: 'pizza_supreme', name: 'Supreme Slice', icon: '🍕', price: 16, kind: 'food' },
      { id: 'garlic_knots', name: 'Garlic Knots', icon: '🥨', price: 7, kind: 'food' },
      { id: 'soda_pop', name: 'Fountain Soda', icon: '🥤', price: 5, kind: 'drink' },
    ],
  },
  arcade: {
    num: 902, kind: 'arcade', name: '🕹️ Retro Arcade', wall: '#181433', floor: '#221c3f',
    npc: 'Pixel', npcTitle: 'Pixel the Attendant', accent: '#a78bfa',
    line: 'Tap a cabinet — 5 coins a play, big wins inside!',
    menu: [],
  },
  vinyl: {
    num: 903, kind: 'vinyl', name: '🎵 Vinyl Vault', wall: '#10222e', floor: '#1a3242',
    npc: 'Nova', npcTitle: 'Nova the Crate-Digger', accent: '#38bdf8',
    line: 'Hop in the listening booth — first spin is free.',
    menu: [],
  },
  ink: {
    num: 904, kind: 'ink', name: '⚡ Ink & Iron Tattoo', wall: '#1c1220', floor: '#241826',
    npc: 'Rook', npcTitle: 'Rook the Ink Master', accent: '#f472b6',
    line: 'Pick a glow tattoo — it follows you around the city.',
    menu: [
      { id: 'ink_flame', name: 'Flame Aura Tattoo', icon: '🔥', price: 60, kind: 'ink' },
      { id: 'ink_halo', name: 'Golden Halo Tattoo', icon: '😇', price: 80, kind: 'ink' },
      { id: 'ink_wings', name: 'Spirit Wings Tattoo', icon: '🕊️', price: 100, kind: 'ink' },
    ],
  },
  boba: {
    num: 905, kind: 'boba', name: '🧋 Boba Break', wall: '#2e1e2a', floor: '#513c33',
    npc: 'Mei', npcTitle: 'Mei the Tea Master', accent: '#fbbf24',
    line: 'Fresh pearls every hour — what are you sipping?',
    menu: [
      { id: 'boba_classic', name: 'Classic Milk Tea', icon: '🧋', price: 9, kind: 'drink' },
      { id: 'boba_taro', name: 'Taro Swirl', icon: '🧋', price: 11, kind: 'drink' },
      { id: 'boba_matcha', name: 'Matcha Cloud', icon: '🍵', price: 12, kind: 'drink' },
    ],
  },
};

export const SHOP_BY_NUM = Object.fromEntries(Object.values(SHOPS).map((s) => [s.num, s]));

// street storefront hit zones (world coords) — matches drawDistricts storefronts
export const SHOP_FRONTS = [
  { kind: 'pizza', x: 900, w: 200 },
  { kind: 'arcade', x: 1120, w: 200 },
  { kind: 'vinyl', x: 1340, w: 200 },
  { kind: 'ink', x: 1560, w: 200 },
  { kind: 'boba', x: 1780, w: 220 },
];

// interior hit zones
export const SHOP_COUNTER = { x: 60, y: 170, w: 280, h: 150 }; // order counter (pizza/ink/boba)
export const ARCADE_CABS = [
  { x: 170, y: 265 }, { x: 320, y: 265 }, { x: 470, y: 265 },
];
export const VINYL_BOOTH = { x: 645, y: 250, w: 130, h: 110 };

const drawNPC = (ctx, x, y, skin, shirt, name, t) => {
  const bob = Math.sin(t * 0.05) * 1.5;
  ctx.fillStyle = 'rgba(0,0,0,0.2)';
  ctx.beginPath(); ctx.ellipse(x, y + 2, 13, 5, 0, 0, Math.PI * 2); ctx.fill();
  // legs
  ctx.fillStyle = '#1f2430';
  ctx.fillRect(x - 7, y - 12, 6, 12);
  ctx.fillRect(x + 1, y - 12, 6, 12);
  ctx.fillStyle = '#0c0f16';
  ctx.fillRect(x - 8, y - 2, 8, 3);
  ctx.fillRect(x + 1, y - 2, 8, 3);
  // torso + apron stripe
  ctx.fillStyle = shirt;
  ctx.fillRect(x - 9, y - 36 + bob, 18, 25);
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(x - 9, y - 24 + bob, 18, 4);
  // arms
  ctx.fillStyle = shirt;
  ctx.fillRect(x - 13, y - 34 + bob, 4, 16 + Math.sin(t * 0.05) * 2);
  ctx.fillRect(x + 9, y - 34 + bob, 4, 16 - Math.sin(t * 0.05) * 2);
  ctx.fillStyle = skin;
  ctx.fillRect(x - 13, y - 18 + bob, 4, 4);
  ctx.fillRect(x + 9, y - 18 + bob, 4, 4);
  // head + hair
  ctx.fillStyle = skin;
  ctx.beginPath(); ctx.arc(x, y - 44 + bob, 9, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#2b2118';
  ctx.beginPath(); ctx.arc(x, y - 47 + bob, 9, Math.PI, 0); ctx.fill();
  ctx.fillRect(x - 9, y - 47 + bob, 18, 3);
  // eyes + smile
  ctx.fillStyle = '#111';
  ctx.fillRect(x - 4, y - 44 + bob, 2.4, 2.4);
  ctx.fillRect(x + 1.6, y - 44 + bob, 2.4, 2.4);
  ctx.strokeStyle = '#7a4a2a'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.arc(x, y - 41 + bob, 3, 0.25 * Math.PI, 0.75 * Math.PI); ctx.stroke();
  ctx.font = '9px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.fillText(name, x, y + 14);
};

const drawBubbleLine = (ctx, x, y, text) => {
  ctx.font = 'bold 10px sans-serif';
  ctx.textAlign = 'center';
  const bw = ctx.measureText(text).width + 16;
  const bx = Math.min(Math.max(x - bw / 2, 6), 794 - bw);
  ctx.fillStyle = 'rgba(255,255,255,0.93)';
  ctx.beginPath();
  if (ctx.roundRect) ctx.roundRect(bx, y - 80, bw, 20, 7); else ctx.rect(bx, y - 80, bw, 20);
  ctx.fill();
  ctx.fillStyle = '#1f2937';
  ctx.fillText(text, bx + bw / 2, y - 66);
};

export function drawShopScene(ctx, kind, t) {
  const s = SHOPS[kind];
  if (!s) return;
  ctx.save();

  // shared counter (except arcade/vinyl which have their own furniture)
  if (kind === 'pizza' || kind === 'ink' || kind === 'boba') {
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.fillRect(70, 232, 260, 14);
    ctx.fillStyle = kind === 'boba' ? '#7c5a49' : '#4a3424';
    ctx.fillRect(70, 246, 260, 52);
    ctx.fillStyle = 'rgba(255,255,255,0.09)';
    ctx.fillRect(78, 252, 244, 6);
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(255,255,255,${0.45 + Math.abs(Math.sin(t * 0.05)) * 0.4})`;
    ctx.fillText('tap the counter to order', 200, 316);
  }

  if (kind === 'pizza') {
    // brick oven with fire
    ctx.fillStyle = '#5b2c1c';
    ctx.beginPath(); ctx.moveTo(560, 280); ctx.lineTo(560, 160); ctx.quadraticCurveTo(640, 96, 720, 160); ctx.lineTo(720, 280); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)'; ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) { ctx.beginPath(); ctx.moveTo(560, 190 + i * 24); ctx.lineTo(720, 190 + i * 24); ctx.stroke(); }
    ctx.fillStyle = '#0a0503';
    ctx.beginPath(); ctx.moveTo(600, 280); ctx.lineTo(600, 210); ctx.quadraticCurveTo(640, 178, 680, 210); ctx.lineTo(680, 280); ctx.closePath(); ctx.fill();
    const fh = 12 + Math.abs(Math.sin(t * 0.11)) * 14;
    ctx.fillStyle = '#f97316';
    ctx.beginPath(); ctx.ellipse(640, 272, 26, fh, 0, Math.PI, 0); ctx.fill();
    ctx.fillStyle = '#fde047';
    ctx.beginPath(); ctx.ellipse(640, 272, 14, fh * 0.55, 0, Math.PI, 0); ctx.fill();
    // pizzas on the counter
    ['#fbbf24', '#f59e0b'].forEach((c, i) => {
      ctx.fillStyle = c;
      ctx.beginPath(); ctx.ellipse(130 + i * 70, 240, 22, 9, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#dc2626';
      for (let p = 0; p < 4; p++) { ctx.beginPath(); ctx.arc(118 + i * 70 + (p * 11) % 26, 238 + (p % 2) * 4, 2.4, 0, Math.PI * 2); ctx.fill(); }
    });
    // hanging menu
    ctx.fillStyle = '#131313'; ctx.fillRect(390, 120, 130, 84);
    ctx.strokeStyle = '#ef4444'; ctx.strokeRect(390, 120, 130, 84);
    ctx.font = 'bold 9px monospace'; ctx.textAlign = 'left'; ctx.fillStyle = '#fecaca';
    ctx.fillText('🍕 Slice ...... 10', 398, 140);
    ctx.fillText('🍕 Supreme .... 16', 398, 156);
    ctx.fillText('🥨 Knots ....... 7', 398, 172);
    ctx.fillText('🥤 Soda ........ 5', 398, 188);
    drawNPC(ctx, 200, 232, '#e8b48a', '#dc2626', 'Tony 👨‍🍳', t);
    if (Math.sin(t * 0.013) > 0.55) drawBubbleLine(ctx, 200, 232, s.line);
  } else if (kind === 'arcade') {
    // neon strip
    ctx.strokeStyle = `rgba(167,139,250,${0.5 + Math.abs(Math.sin(t * 0.06)) * 0.5})`;
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(40, 118); ctx.lineTo(760, 118); ctx.stroke();
    // cabinets
    ARCADE_CABS.forEach((cab, i) => {
      const cx2 = cab.x; const cy = cab.y;
      ctx.fillStyle = ['#3b0764', '#164e63', '#7f1d1d'][i];
      ctx.fillRect(cx2 - 34, cy - 130, 68, 130);
      ctx.fillStyle = '#0b0714';
      ctx.fillRect(cx2 - 26, cy - 118, 52, 44);
      // animated screen
      for (let p = 0; p < 8; p++) {
        const on = Math.floor(t * 0.15 + p + i * 3) % 3 === 0;
        ctx.fillStyle = on ? ['#a78bfa', '#22d3ee', '#f87171'][(p + i) % 3] : 'rgba(255,255,255,0.08)';
        ctx.fillRect(cx2 - 22 + (p % 4) * 12, cy - 112 + Math.floor(p / 4) * 16, 8, 10);
      }
      ctx.fillStyle = '#d1d5db';
      ctx.fillRect(cx2 - 26, cy - 66, 52, 10);
      ctx.fillStyle = '#ef4444'; ctx.beginPath(); ctx.arc(cx2 - 10, cy - 61, 4, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#fbbf24'; ctx.beginPath(); ctx.arc(cx2 + 10, cy - 61, 4, 0, Math.PI * 2); ctx.fill();
      const gl = 0.4 + Math.abs(Math.sin(t * 0.07 + i)) * 0.6;
      ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillStyle = `rgba(253,224,71,${gl})`;
      ctx.fillText('INSERT COIN · 5', cx2, cy - 42);
      ctx.fillText(['SPACE RAID', 'TURBO GP', 'PIXL PUNCH'][i], cx2, cy + 12);
    });
    ctx.font = 'bold 9px sans-serif'; ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(255,255,255,${0.45 + Math.abs(Math.sin(t * 0.05)) * 0.4})`;
    ctx.fillText('tap a cabinet to play — 5 coins, win up to 25', 320, 300);
    drawNPC(ctx, 660, 260, '#caa47c', '#7c3aed', 'Pixel 🕹️', t);
    if (Math.sin(t * 0.011) > 0.6) drawBubbleLine(ctx, 660, 260, s.line);
  } else if (kind === 'vinyl') {
    // record crates
    for (let i = 0; i < 3; i++) {
      const bx = 90 + i * 150;
      ctx.fillStyle = '#3a2a1a'; ctx.fillRect(bx, 210, 110, 70);
      for (let r = 0; r < 8; r++) {
        ctx.fillStyle = ['#111', '#1c1c1c'][r % 2];
        ctx.fillRect(bx + 8 + r * 12, 216, 10, 58);
        ctx.fillStyle = ['#ef4444', '#38bdf8', '#a3e635', '#fbbf24', '#f472b6'][r % 5];
        ctx.fillRect(bx + 8 + r * 12, 216, 10, 7);
      }
    }
    // gold records on the wall
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = '#3a2a12'; ctx.fillRect(150 + i * 130, 130, 56, 56);
      ctx.fillStyle = '#fbbf24';
      ctx.beginPath(); ctx.arc(178 + i * 130, 158, 20, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#78350f';
      ctx.beginPath(); ctx.arc(178 + i * 130, 158, 6, 0, Math.PI * 2); ctx.fill();
    }
    // listening booth (right)
    ctx.fillStyle = '#0d1b24'; ctx.fillRect(645, 160, 130, 140);
    ctx.strokeStyle = `rgba(56,189,248,${0.5 + Math.abs(Math.sin(t * 0.06)) * 0.5})`;
    ctx.lineWidth = 2.5; ctx.strokeRect(645, 160, 130, 140);
    // turntable
    ctx.fillStyle = '#1f2937'; ctx.fillRect(665, 236, 90, 40);
    ctx.fillStyle = '#111';
    ctx.beginPath(); ctx.arc(700, 256, 15, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = '#38bdf8'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.arc(700, 256, 15, t * 0.1, t * 0.1 + 1.2); ctx.stroke();
    ctx.font = '15px sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('🎧', 738, 262);
    ctx.font = 'bold 9px monospace';
    ctx.fillStyle = `rgba(125,211,252,${0.5 + Math.abs(Math.sin(t * 0.05)) * 0.5})`;
    ctx.fillText('LISTENING BOOTH', 710, 178);
    ctx.font = 'bold 8px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText('tap to spin a record — free', 710, 192);
    drawNPC(ctx, 420, 300, '#8a5a34', '#0ea5e9', 'Nova 🎵', t);
    if (Math.sin(t * 0.012) > 0.6) drawBubbleLine(ctx, 420, 300, s.line);
  } else if (kind === 'ink') {
    // flash art wall
    const art = ['💀', '🌹', '⚓', '🐉', '⚡', '🦋'];
    art.forEach((a, i) => {
      const ax = 420 + (i % 3) * 110; const ay = 130 + Math.floor(i / 3) * 88;
      ctx.fillStyle = '#efe6d5'; ctx.fillRect(ax, ay, 78, 66);
      ctx.strokeStyle = '#111'; ctx.strokeRect(ax, ay, 78, 66);
      ctx.font = '26px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText(a, ax + 39, ay + 44);
    });
    // tattoo chair
    ctx.fillStyle = '#18181b';
    ctx.fillRect(120, 330, 110, 18);
    ctx.fillRect(150, 300, 60, 34);
    ctx.fillStyle = '#7f1d1d';
    ctx.fillRect(126, 322, 98, 12);
    // neon bolt
    const gl = 0.5 + Math.abs(Math.sin(t * 0.08)) * 0.5;
    ctx.font = 'bold 30px sans-serif'; ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(244,114,182,${gl})`;
    ctx.shadowColor = '#f472b6'; ctx.shadowBlur = 14 * gl;
    ctx.fillText('⚡', 320, 160);
    ctx.shadowBlur = 0;
    drawNPC(ctx, 200, 232, '#caa47c', '#111827', 'Rook ⚡', t);
    if (Math.sin(t * 0.014) > 0.55) drawBubbleLine(ctx, 200, 232, s.line);
  } else if (kind === 'boba') {
    // pearl jars on shelf
    ctx.fillStyle = '#3f2d22'; ctx.fillRect(420, 170, 320, 8);
    ['#0f0a08', '#7c3aed', '#166534', '#b45309', '#be185d'].forEach((c, i) => {
      const jx = 440 + i * 60;
      ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.fillRect(jx, 128, 34, 42);
      ctx.fillStyle = c;
      for (let p = 0; p < 8; p++) {
        ctx.beginPath(); ctx.arc(jx + 7 + (p % 4) * 7, 158 - Math.floor(p / 4) * 8, 3, 0, Math.PI * 2); ctx.fill();
      }
    });
    // giant boba cup sign
    const bob = Math.sin(t * 0.04) * 4;
    ctx.fillStyle = 'rgba(251,191,36,0.18)';
    ctx.fillRect(560, 196 + bob, 64, 84);
    ctx.fillStyle = '#92400e';
    for (let p = 0; p < 6; p++) { ctx.beginPath(); ctx.arc(572 + (p % 3) * 20, 264 + bob + Math.floor(p / 3) * 12 + Math.sin(t * 0.09 + p) * 3, 5, 0, Math.PI * 2); ctx.fill(); }
    ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 3;
    ctx.strokeRect(560, 196 + bob, 64, 84);
    ctx.beginPath(); ctx.moveTo(592, 196 + bob); ctx.lineTo(604, 150 + bob); ctx.stroke();
    drawNPC(ctx, 200, 232, '#e8b48a', '#f59e0b', 'Mei 🧋', t);
    if (Math.sin(t * 0.012) > 0.55) drawBubbleLine(ctx, 200, 232, s.line);
  }
  ctx.restore();
}
